import { useTranslation } from "react-i18next"
import { RiSettings4Line } from "@remixicon/react"
import { addDays, addMonths, addWeeks, format, subDays, subMonths, subWeeks } from "date-fns"

import useRouter from "@/hooks/router/useRoute"
import Tab from "@/components/tab/Tab"
import IconButton from "@/components/button/IconButton"
import Tooltip from "@/components/tooltip/Tooltip"
import { useDashboardStore } from "@/stores/dashboard"
import { useActivityTypeStatsQuery } from "@/hooks/api/useActivityQuery"
import { useSessionTypeStatsQuery } from "@/hooks/api/useSessionQuery"
import { useCalendarNav } from "@/app/home/hooks/useCalendarNav"
import type { WidgetItem, CalendarViewType } from "@/types/dashboard"

const CALENDAR_VIEW_TABS = [
  { value: "monthly", label: "Month" },
  { value: "weekly", label: "Week" },
  { value: "daily", label: "Schedule" },
]

/**
 * 위젯 카드 헤더 오른쪽에 표시할 액션.
 * 위젯 타입별로 분기하여 컴팩트한 컨트롤을 렌더링.
 */
export default function WidgetHeaderAction({ widget }: { widget: WidgetItem }) {
  const { t } = useTranslation()
  const router = useRouter()
  const updateWidgetOptions = useDashboardStore((s) => s.updateWidgetOptions)

  switch (widget.widgetType) {
    case "calendar": {
      const view = (widget.options.defaultView as CalendarViewType) ?? "monthly"
      return (
        <div className="CalendarWidgetHeader">
          <CalendarDateNav view={view} />
          <Tab
            type="filled"
            list={CALENDAR_VIEW_TABS.map((tab) => ({
              value: tab.value,
              label: t(`calendarView.${tab.value}`, tab.label),
            }))}
            value={view}
            onChange={(_, newValue) => updateWidgetOptions(widget.id, { defaultView: newValue })}
          />
        </div>
      )
    }

    case "activity-distribution":
      return <ActivityDistributionTotal />

    case "session-distribution":
      return <SessionDistributionTotal />

    case "notice":
      return (
        <IconButton
          type="ghost"
          icon="arrow-right"
          size="sm"
          onClick={() => router.push("/notice")}
        />
      )

    case "quick-report":
      return (
        <Tooltip title={t("managePresets", "Manage Presets")} size="small">
          <button
            className="QuickReportWidget__manage-btn"
            onClick={() => router.push("/settings/report/filter-preset")}>
            <RiSettings4Line size={20} />
          </button>
        </Tooltip>
      )

    default:
      return null
  }
}

function TotalLabel({ total }: { total: number }) {
  const { t } = useTranslation()
  return (
    <div className="WidgetHeaderTotal">
      <span className="WidgetHeaderTotal__label">{t("total")}</span>
      <span className="WidgetHeaderTotal__value">{total}</span>
    </div>
  )
}

function ActivityDistributionTotal() {
  const { data } = useActivityTypeStatsQuery()
  const total = data ? Object.values(data).reduce((sum, n) => sum + (n ?? 0), 0) : 0
  return <TotalLabel total={total} />
}

function CalendarDateNav({ view }: { view: CalendarViewType }) {
  const { currentDate, setCurrentDate } = useCalendarNav()

  const label = format(currentDate, view === "daily" ? "MMM d, yyyy" : "MMMM yyyy")

  const handlePrev = () => {
    if (view === "monthly") setCurrentDate(subMonths(currentDate, 1))
    else if (view === "weekly") setCurrentDate(subWeeks(currentDate, 1))
    else setCurrentDate(subDays(currentDate, 1))
  }
  const handleNext = () => {
    if (view === "monthly") setCurrentDate(addMonths(currentDate, 1))
    else if (view === "weekly") setCurrentDate(addWeeks(currentDate, 1))
    else setCurrentDate(addDays(currentDate, 1))
  }

  return (
    <div className="CalendarDateNav">
      <IconButton type="ghost" icon="arrow-left" size="sm" onClick={handlePrev} />
      <span className="CalendarDateNav__title">{label}</span>
      <IconButton type="ghost" icon="arrow-right" size="sm" onClick={handleNext} />
    </div>
  )
}

function SessionDistributionTotal() {
  const { data } = useSessionTypeStatsQuery()
  const total =
    data?.filter((s) => s.name !== "null").reduce((sum, s) => sum + (s.count ?? 0), 0) ?? 0
  return <TotalLabel total={total} />
}
